/**
 * Consent form interactive form + builder — renders the inline preview with
 * editable fields, and wires it together with the PDF via `withDocumentEditor`.
 */
import { Plus, X } from 'lucide-react'

import { TimePicker } from '@/frontend/components/time-picker'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Input } from '@/frontend/components/ui/input'
import { Label } from '@/frontend/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'
import { CatalogProductAutocomplete } from '@/frontend/features/inventory/catalog-product-autocomplete'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignaturePreview } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetailsPreview } from '@/frontend/features/printing/documents/blocks/patient-details'
import {
  FEEDING_CONSENT_TEXT,
  resolveClause,
} from '@/frontend/features/printing/documents/consent/clauses'
import { ConsentPdf } from '@/frontend/features/printing/documents/consent/consent-template.print'
import type {
  ConsentFormFields,
  ConsentTemplateConfig,
} from '@/frontend/features/printing/documents/consent/types'
import {
  defaultConsentFormFields,
  emptyMedicationRows,
} from '@/frontend/features/printing/documents/consent/types'
import type {
  DocumentContext,
  DocumentFormProps,
} from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

/** Inline YES/NO radio group used in the pre-op questions. */
function YesNo({
  value,
  onChange,
  id,
}: {
  value: string
  onChange: (v: string) => void
  id: string
}) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="inline-flex gap-3">
      <div className="flex items-center gap-1">
        <RadioGroupItem value="yes" id={`${id}-yes`} />
        <Label htmlFor={`${id}-yes`} className="font-normal">
          YES
        </Label>
      </div>
      <div className="flex items-center gap-1">
        <RadioGroupItem value="no" id={`${id}-no`} />
        <Label htmlFor={`${id}-no`} className="font-normal">
          NO
        </Label>
      </div>
    </RadioGroup>
  )
}

function ConsentForm({
  config,
  context,
  value,
  onChange,
}: DocumentFormProps<ConsentFormFields> & { config: ConsentTemplateConfig }) {
  const { patient, owner, clinic } = context
  const name = patient.name

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 text-center text-lg font-bold underline">{config.title}</h1>

      <OwnerDetailsPreview owner={owner} />
      <div className="mt-3">
        <PatientDetailsPreview
          patient={patient}
          weight={context.weight}
          showDob
          showWeight
          showMicrochip
        />
      </div>

      {/* Consent paragraph */}
      <div className="mt-4">
        <config.renderConsentPreview patientName={name} />
      </div>

      {/* Pre-op questions */}
      <ul className="mt-4 list-disc space-y-4 pl-6">
        {/* Last eat */}
        <li className="flex flex-wrap items-center gap-1">
          What time did {name} <span className="font-semibold">last eat</span> anything?
          <TimePicker
            className="inline-flex w-28"
            placeholder="time"
            value={value.lastEatTime || undefined}
            onChange={(t) => onChange({ ...value, lastEatTime: t })}
            snap={5}
          />
        </li>

        {/* Current medication */}
        <li>
          <p>
            Is {name} currently on any medication? If yes, please state what this is and time last
            given below.{' '}
            <YesNo
              value={value.onMedication}
              onChange={(v) => onChange({ ...value, onMedication: v as 'yes' | 'no' })}
              id="on-medication"
            />
          </p>
          <div className="mt-2 space-y-2">
            {value.medications.map((med, i) => (
              <div key={med.key} className="flex gap-2">
                <CatalogProductAutocomplete
                  className="flex-1"
                  placeholder="Medication"
                  value={med.name}
                  onChange={(name) => {
                    const updated = [...value.medications]
                    updated[i] = { ...updated[i], name }
                    onChange({ ...value, medications: updated })
                  }}
                />
                <Input
                  className="w-48"
                  placeholder="Date & time last given"
                  value={med.dateTimeGiven}
                  onChange={(e) => {
                    const updated = [...value.medications]
                    updated[i] = { ...updated[i], dateTimeGiven: e.target.value }
                    onChange({ ...value, medications: updated })
                  }}
                />
                <Button
                  variant="ghost"
                  onClick={() => {
                    const updated = value.medications.filter((_, j) => j !== i)
                    onChange({ ...value, medications: updated })
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() =>
                onChange({
                  ...value,
                  medications: [
                    ...value.medications,
                    { key: crypto.randomUUID(), name: '', dateTimeGiven: '' },
                  ],
                })
              }
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add medication
            </Button>
          </div>
        </li>

        {/* Allergies */}
        <li>
          <p>
            Does {name} have any allergies?{' '}
            <YesNo
              value={value.hasAllergies}
              onChange={(v) => onChange({ ...value, hasAllergies: v as 'yes' | 'no' })}
              id="allergies"
            />
          </p>
          {value.hasAllergies === 'yes' && (
            <Input
              className="mt-2"
              placeholder="Details..."
              value={value.allergiesDetails}
              onChange={(e) => onChange({ ...value, allergiesDetails: e.target.value })}
            />
          )}
        </li>

        {/* Concerning signs */}
        <li>
          <p>
            Has {name} have any new or concerning signs/symptoms since they were last seen by the
            vet, or is there anything you would like the team to be aware of?{' '}
            <YesNo
              value={value.hasConcerningSigns}
              onChange={(v) => onChange({ ...value, hasConcerningSigns: v as 'yes' | 'no' })}
              id="concerning-signs"
            />
          </p>
          {value.hasConcerningSigns === 'yes' && (
            <Input
              className="mt-2"
              placeholder="Details..."
              value={value.concerningSignsDetails}
              onChange={(e) => onChange({ ...value, concerningSignsDetails: e.target.value })}
            />
          )}
        </li>

        {/* Feeding consent */}
        <li>
          <p>
            {resolveClause(FEEDING_CONSENT_TEXT, name)}{' '}
            <YesNo
              value={value.feedingConsent}
              onChange={(v) => onChange({ ...value, feedingConsent: v as 'yes' | 'no' })}
              id="feeding-consent"
            />
          </p>
        </li>

        {/* Procedure-specific clauses */}
        {config.clauses.map((clause) => {
          const text = resolveClause(clause, name)
          return <li key={text.slice(0, 40)}>{text}</li>
        })}
      </ul>

      {/* Pre-anaesthetic blood opt-in */}
      <div className="mt-4 flex items-start gap-2">
        <Checkbox
          checked={value.optInBloodTests}
          onCheckedChange={(checked) => onChange({ ...value, optInBloodTests: checked === true })}
          id="opt-in-blood"
        />
        <Label htmlFor="opt-in-blood" className="font-normal italic">
          Please tick this box if you would like to <span className="font-semibold">opt in</span> to
          pre-anaesthetic blood tests.
        </Label>
      </div>

      <OwnerSignaturePreview owner={context.owner} />
    </DocumentPreview>
  )
}

/** Build a pre-op consent form from a procedure-specific config. */
export function buildPreOpConsentForm(config: ConsentTemplateConfig) {
  return withDocumentEditor({
    id: config.id,
    label: config.label,
    clinic: config.clinic,
    defaultFields: { ...defaultConsentFormFields, medications: emptyMedicationRows() },
    Preview: (props: DocumentFormProps<ConsentFormFields>) => (
      <ConsentForm {...props} config={config} />
    ),
    Pdf: ({ context, fields }: { context: DocumentContext; fields: ConsentFormFields }) => (
      <ConsentPdf config={config} context={context} fields={fields} />
    ),
  })
}
